import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Tabs,
    Tab,
    Typography,
    Card,
    CardContent,
    Stack,
    Chip,
    Divider,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Skeleton,
    Alert,
    Snackbar,
    CircularProgress,
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    Warning as WarningIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    CancelOutlined as CancelOutlinedIcon,
    Star as StarIcon,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { amber } from "@mui/material/colors";
import { usePermissions } from "../hooks/usePermissions";
import VehicleService from "../services/Vehicle.service";

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayString() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtDate(str) {
    if (!str) return "";
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

function parseLocalDate(str) {
    if (!str) return null;
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
}

// ── TabPanel ──────────────────────────────────────────────────────────────────

function TabPanel({ value, index, children }) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ── Vehicle accordion card ─────────────────────────────────────────────────────

function VehicleCard({ vehicle, issues, onReportIssue, onLogService, onLogCleaning, onLogInspection }) {
    const [expanded, setExpanded] = useState(false);
    const hasIssue = issues.some((i) => i.vehicle?.id === vehicle.id);

    return (
        <Accordion
            expanded={expanded}
            onChange={(_, v) => setExpanded(v)}
            disableGutters
            elevation={0}
            sx={{
                mb: 1,
                border: 1,
                borderColor: hasIssue ? "warning.main" : "divider",
                borderRadius: "12px !important",
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" flex={1}>
                    {hasIssue && (
                        <WarningIcon fontSize="small" sx={{ color: amber[500], flexShrink: 0 }} />
                    )}
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        >
                            {vehicle.nickname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                        </Typography>
                    </Box>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                {vehicle.image && (
                    <Box
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "action.hover",
                        }}
                    >
                        <img
                            src={vehicle.image}
                            alt={vehicle.nickname}
                            style={{ width: "100%", maxHeight: 220, objectFit: "cover", display: "block" }}
                        />
                    </Box>
                )}
                {hasIssue && (
                    <Chip
                        size="small"
                        icon={<WarningIcon />}
                        label="Open Issue"
                        color="warning"
                        sx={{ mb: 1.5 }}
                    />
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {vehicle.color && <>Color: {vehicle.color}<br /></>}
                    {vehicle.license_plate && <>License: {vehicle.license_plate}<br /></>}
                    {vehicle.vin_number && <>VIN: {vehicle.vin_number}<br /></>}
                    {vehicle.max_passagers && <>Max Passengers: {vehicle.max_passagers}<br /></>}
                    {vehicle.max_weight && <>Max Weight: {vehicle.max_weight}<br /></>}
                    {vehicle.toll_tag && <>Toll Tag: {vehicle.toll_tag}<br /></>}
                    {vehicle.assignment && (
                        <>Assigned to: {vehicle.assignment.first_name} {vehicle.assignment.last_name}</>
                    )}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onReportIssue(vehicle.id)}
                    >
                        Report Issue
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onLogService(vehicle.id)}
                    >
                        Log Service
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onLogCleaning(vehicle.id)}
                    >
                        Log Cleaning
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onLogInspection(vehicle.id)}
                    >
                        Log Inspection
                    </Button>
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}

// ── Record cards ──────────────────────────────────────────────────────────────

function IssueCard({ issue, onDelete, canDelete }) {
    return (
        <Card sx={{ mb: 1, boxShadow: "none", border: 1, borderColor: "divider", borderRadius: 2 }}>
            <CardContent sx={{ pb: "12px !important" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1} minWidth={0}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        >
                            {issue.vehicle?.nickname}
                        </Typography>
                        <Typography variant="body2">{issue.description}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Reported: {fmtDate(issue.date)}
                        </Typography>
                    </Box>
                    {canDelete && (
                        <IconButton size="small" color="error" onClick={() => onDelete(issue)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

function ServiceCard({ service, onDelete, canDelete }) {
    return (
        <Card sx={{ mb: 1, boxShadow: "none", border: 1, borderColor: "divider", borderRadius: 2 }}>
            <CardContent sx={{ pb: "12px !important" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1} minWidth={0}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        >
                            {service.vehicle?.nickname}
                        </Typography>
                        <Typography variant="body2">{service.description}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Serviced: {fmtDate(service.date)}
                        </Typography>
                    </Box>
                    {canDelete && (
                        <IconButton size="small" color="error" onClick={() => onDelete(service)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

const CLEANING_FIELDS = [
    { key: "wash_exterior", label: "Wash Exterior" },
    { key: "wash_interior", label: "Wash Interior" },
    { key: "clean_windows", label: "Clean Windows" },
    { key: "wash_seats", label: "Wash Seats" },
    { key: "vacuum_seats", label: "Vacuum Seats" },
    { key: "vacuum_floor", label: "Vacuum Floor" },
];

function CleaningCard({ cleaning, onDelete, canDelete }) {
    const [showDetails, setShowDetails] = useState(false);
    const isFull = CLEANING_FIELDS.every((f) => cleaning[f.key]);

    return (
        <Card
            sx={{
                mb: 1,
                boxShadow: "none",
                border: 1,
                borderColor: isFull ? "warning.main" : "divider",
                borderRadius: 2,
            }}
        >
            <CardContent sx={{ pb: "12px !important" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1} minWidth={0}>
                        <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
                            {isFull && (
                                <StarIcon fontSize="small" sx={{ color: amber[500] }} />
                            )}
                            <Typography
                                variant="subtitle2"
                                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                            >
                                {cleaning.vehicle?.nickname}
                            </Typography>
                        </Stack>
                        <Chip
                            size="small"
                            label={isFull ? "Full Cleaning" : "Partial Cleaning"}
                            color={isFull ? "warning" : "default"}
                            sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                            Cleaned: {fmtDate(cleaning.date)}
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => setShowDetails((v) => !v)}
                            sx={{ p: 0, mt: 0.5, textTransform: "none", minWidth: 0 }}
                        >
                            {showDetails ? "Hide details" : "Show details"}
                        </Button>
                        {showDetails && (
                            <Box sx={{ mt: 0.75 }}>
                                {CLEANING_FIELDS.map((f) => (
                                    <Stack
                                        key={f.key}
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                    >
                                        {cleaning[f.key] ? (
                                            <CheckCircleOutlineIcon
                                                fontSize="small"
                                                color="success"
                                            />
                                        ) : (
                                            <CancelOutlinedIcon fontSize="small" color="error" />
                                        )}
                                        <Typography variant="caption">{f.label}</Typography>
                                    </Stack>
                                ))}
                                {cleaning.other && cleaning.other_description && (
                                    <Typography variant="caption" color="text.secondary">
                                        Other: {cleaning.other_description}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                    {canDelete && (
                        <IconButton size="small" color="error" onClick={() => onDelete(cleaning)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

function InspectionCard({ inspection, onDelete, canDelete }) {
    const expDate = parseLocalDate(inspection.expiration_date);
    const isExpired = expDate && expDate < new Date();

    return (
        <Card
            sx={{
                mb: 1,
                boxShadow: "none",
                border: 1,
                borderColor: isExpired ? "error.main" : "divider",
                borderRadius: 2,
            }}
        >
            <CardContent sx={{ pb: "12px !important" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1} minWidth={0}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        >
                            {inspection.vehicle?.nickname}
                        </Typography>
                        <Typography variant="body2">{inspection.description}</Typography>
                        {expDate && (
                            <Chip
                                size="small"
                                label={`Expires ${formatDistanceToNow(expDate, { addSuffix: true })}`}
                                color={isExpired ? "error" : "warning"}
                                sx={{ mt: 0.5, mb: 0.25 }}
                            />
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                            Date: {fmtDate(inspection.date)}
                            {inspection.expiration_date && ` · Exp: ${fmtDate(inspection.expiration_date)}`}
                        </Typography>
                    </Box>
                    {canDelete && (
                        <IconButton size="small" color="error" onClick={() => onDelete(inspection)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

// ── Add dialogs ───────────────────────────────────────────────────────────────

function AddIssueDialog({ open, onClose, onSave, vehicles, initialVehicleId }) {
    const [form, setForm] = useState({ vehicle: "", description: "", date: todayString() });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setForm({ vehicle: initialVehicleId || "", description: "", date: todayString() });
    }, [open, initialVehicleId]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(form);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                Report Issue
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Vehicle"
                        select
                        value={form.vehicle}
                        onChange={(e) => setForm((f) => ({ ...f, vehicle: e.target.value }))}
                        fullWidth
                        size="small"
                    >
                        {vehicles.map((v) => (
                            <MenuItem key={v.id} value={v.id}>
                                {v.nickname}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Description"
                        multiline
                        rows={3}
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        fullWidth
                        size="small"
                        inputProps={{ maxLength: 500 }}
                    />
                    <TextField
                        label="Date"
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!form.vehicle || !form.description || saving}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function AddServiceDialog({ open, onClose, onSave, vehicles, initialVehicleId }) {
    const [form, setForm] = useState({ vehicle: "", description: "", date: todayString() });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setForm({ vehicle: initialVehicleId || "", description: "", date: todayString() });
    }, [open, initialVehicleId]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(form);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                Log Service
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Vehicle"
                        select
                        value={form.vehicle}
                        onChange={(e) => setForm((f) => ({ ...f, vehicle: e.target.value }))}
                        fullWidth
                        size="small"
                    >
                        {vehicles.map((v) => (
                            <MenuItem key={v.id} value={v.id}>
                                {v.nickname}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Description"
                        multiline
                        rows={3}
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        fullWidth
                        size="small"
                        inputProps={{ maxLength: 500 }}
                    />
                    <TextField
                        label="Date"
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!form.vehicle || !form.description || saving}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const CLEANING_KEYS = [
    "wash_exterior",
    "wash_interior",
    "clean_windows",
    "wash_seats",
    "vacuum_seats",
    "vacuum_floor",
];
const CLEANING_KEY_LABELS = [
    "Wash Exterior",
    "Wash Interior",
    "Clean Windows",
    "Wash Seats",
    "Vacuum Seats",
    "Vacuum Floor",
];

const blankCleaning = {
    vehicle: "",
    date: "",
    wash_exterior: false,
    wash_interior: false,
    clean_windows: false,
    wash_seats: false,
    vacuum_seats: false,
    vacuum_floor: false,
    other: false,
    other_description: "",
};

function AddCleaningDialog({ open, onClose, onSave, vehicles, initialVehicleId }) {
    const [form, setForm] = useState(blankCleaning);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({ ...blankCleaning, vehicle: initialVehicleId || "", date: todayString() });
        }
    }, [open, initialVehicleId]);

    const toggle = (key) => setForm((f) => ({ ...f, [key]: !f[key] }));

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(form);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                Log Cleaning
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Vehicle"
                        select
                        value={form.vehicle}
                        onChange={(e) => setForm((f) => ({ ...f, vehicle: e.target.value }))}
                        fullWidth
                        size="small"
                    >
                        {vehicles.map((v) => (
                            <MenuItem key={v.id} value={v.id}>
                                {v.nickname}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Date"
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                    <FormGroup>
                        {CLEANING_KEYS.map((key, i) => (
                            <FormControlLabel
                                key={key}
                                control={
                                    <Checkbox
                                        checked={form[key]}
                                        onChange={() => toggle(key)}
                                        size="small"
                                    />
                                }
                                label={CLEANING_KEY_LABELS[i]}
                            />
                        ))}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.other}
                                    onChange={() => toggle("other")}
                                    size="small"
                                />
                            }
                            label="Other"
                        />
                    </FormGroup>
                    {form.other && (
                        <TextField
                            label="Other description"
                            value={form.other_description}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, other_description: e.target.value }))
                            }
                            fullWidth
                            size="small"
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!form.vehicle || !form.date || saving}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function AddInspectionDialog({ open, onClose, onSave, vehicles, initialVehicleId }) {
    const [form, setForm] = useState({
        vehicle: "",
        description: "",
        date: todayString(),
        expiration_date: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                vehicle: initialVehicleId || "",
                description: "",
                date: todayString(),
                expiration_date: "",
            });
        }
    }, [open, initialVehicleId]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(form);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                Log Inspection
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Vehicle"
                        select
                        value={form.vehicle}
                        onChange={(e) => setForm((f) => ({ ...f, vehicle: e.target.value }))}
                        fullWidth
                        size="small"
                    >
                        {vehicles.map((v) => (
                            <MenuItem key={v.id} value={v.id}>
                                {v.nickname}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Description"
                        multiline
                        rows={2}
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        fullWidth
                        size="small"
                        inputProps={{ maxLength: 500 }}
                    />
                    <TextField
                        label="Inspection Date"
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Expiration Date"
                        type="date"
                        value={form.expiration_date}
                        onChange={(e) =>
                            setForm((f) => ({ ...f, expiration_date: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!form.vehicle || !form.description || saving}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function DeleteDialog({ open, onClose, onConfirm, message }) {
    const [deleting, setDeleting] = useState(false);

    const handle = async () => {
        setDeleting(true);
        try {
            await onConfirm();
            onClose();
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                Confirm Delete
            </DialogTitle>
            <DialogContent>
                <Typography>{message}</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={deleting}>Cancel</Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handle}
                    disabled={deleting}
                    startIcon={deleting ? <CircularProgress size={16} /> : null}
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Insurance info ────────────────────────────────────────────────────────────

function EditInsuranceDialog({ open, onClose, insurance, onSave }) {
    const [form, setForm] = useState({
        company: "",
        policy_number: "",
        company_number: "",
        effective_date: "",
        expiration_date: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && insurance) {
            setForm({
                company: insurance.company || "",
                policy_number: insurance.policy_number || "",
                company_number: insurance.company_number || "",
                effective_date: insurance.effective_date || "",
                expiration_date: insurance.expiration_date || "",
            });
        }
    }, [open, insurance]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(form);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                Edit Vehicle Insurance
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Company"
                        value={form.company}
                        onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                        fullWidth
                        size="small"
                        inputProps={{ maxLength: 200 }}
                    />
                    <TextField
                        label="Policy #"
                        value={form.policy_number}
                        onChange={(e) => setForm((f) => ({ ...f, policy_number: e.target.value }))}
                        fullWidth
                        size="small"
                        inputProps={{ maxLength: 100 }}
                    />
                    <TextField
                        label="Company #"
                        value={form.company_number}
                        onChange={(e) => setForm((f) => ({ ...f, company_number: e.target.value }))}
                        fullWidth
                        size="small"
                        inputProps={{ maxLength: 100 }}
                    />
                    <TextField
                        label="Effective Date"
                        type="date"
                        value={form.effective_date}
                        onChange={(e) =>
                            setForm((f) => ({ ...f, effective_date: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Expiration Date"
                        type="date"
                        value={form.expiration_date}
                        onChange={(e) =>
                            setForm((f) => ({ ...f, expiration_date: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function InsurancePanel({ canManage, toast }) {
    const [insurance, setInsurance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        VehicleService.getInsurance()
            .then((res) => setInsurance(res.data))
            .catch(() => toast("Failed to load insurance info.", "error"))
            .finally(() => setLoading(false));
    }, [toast]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSave = async (data) => {
        try {
            const res = await VehicleService.updateInsurance(data);
            setInsurance(res.data);
            toast("Insurance info updated.");
        } catch {
            toast("Failed to update insurance info.", "error");
        }
    };

    return (
        <Card sx={{ boxShadow: "none", border: 1, borderColor: "divider", borderRadius: 2 }}>
            <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                    >
                        Vehicle Insurance
                    </Typography>
                    {canManage && !loading && (
                        <Button size="small" onClick={() => setEditOpen(true)}>
                            Edit
                        </Button>
                    )}
                </Stack>
                {loading ? (
                    <Stack spacing={1}>
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="40%" />
                        <Skeleton variant="text" width="40%" />
                        <Skeleton variant="text" width="30%" />
                        <Skeleton variant="text" width="30%" />
                    </Stack>
                ) : (
                    <Stack spacing={0.75}>
                        <Typography variant="body2">
                            <strong>Company:</strong> {insurance?.company || "—"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Policy #:</strong> {insurance?.policy_number || "—"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Company #:</strong> {insurance?.company_number || "—"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Effective:</strong> {fmtDate(insurance?.effective_date) || "—"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Expires:</strong> {fmtDate(insurance?.expiration_date) || "—"}
                        </Typography>
                    </Stack>
                )}
            </CardContent>
            <EditInsuranceDialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                insurance={insurance}
                onSave={handleSave}
            />
        </Card>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const TABS = ["Fleet", "Issues", "Services", "Cleanings", "Inspections", "Insurance"];

export default function FieldVehicles() {
    const { isManagement, isAdmin } = usePermissions();
    const canManage = isManagement || isAdmin;

    const [tab, setTab] = useState(0);
    const [vehicles, setVehicles] = useState([]);
    const [issues, setIssues] = useState([]);
    const [services, setServices] = useState([]);
    const [cleanings, setCleanings] = useState([]);
    const [inspections, setInspections] = useState([]);
    const [loading, setLoading] = useState(true);

    const [addIssue, setAddIssue] = useState({ open: false, vehicleId: "" });
    const [addService, setAddService] = useState({ open: false, vehicleId: "" });
    const [addCleaning, setAddCleaning] = useState({ open: false, vehicleId: "" });
    const [addInspection, setAddInspection] = useState({ open: false, vehicleId: "" });
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        type: "",
        item: null,
        message: "",
    });

    const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
    const toast = (message, severity = "success") =>
        setSnack({ open: true, message, severity });

    const loadAll = useCallback(() => {
        setLoading(true);
        Promise.all([
            VehicleService.listVehicles(),
            VehicleService.listIssues(),
            VehicleService.listServices(),
            VehicleService.listCleanings(),
            VehicleService.listInspections(),
        ])
            .then(([vRes, iRes, sRes, cRes, insRes]) => {
                setVehicles(vRes.data.results ?? vRes.data);
                setIssues(iRes.data.results ?? iRes.data);
                setServices(sRes.data.results ?? sRes.data);
                setCleanings(cRes.data.results ?? cRes.data);
                setInspections(insRes.data.results ?? insRes.data);
            })
            .catch(() => toast("Failed to load vehicle data.", "error"))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const handleAddIssue = async (data) => {
        await VehicleService.createIssue(data);
        toast("Issue reported.");
        loadAll();
    };
    const handleAddService = async (data) => {
        await VehicleService.createService(data);
        toast("Service logged.");
        loadAll();
    };
    const handleAddCleaning = async (data) => {
        await VehicleService.createCleaning(data);
        toast("Cleaning logged.");
        loadAll();
    };
    const handleAddInspection = async (data) => {
        await VehicleService.createInspection(data);
        toast("Inspection logged.");
        loadAll();
    };

    const handleDelete = async () => {
        const { type, item } = deleteDialog;
        if (type === "issue") await VehicleService.deleteIssue(item.id);
        else if (type === "service") await VehicleService.deleteService(item.id);
        else if (type === "cleaning") await VehicleService.deleteCleaning(item.id);
        else if (type === "inspection") await VehicleService.deleteInspection(item.id);
        toast("Deleted.", "warning");
        loadAll();
    };

    const confirmDelete = (type, item, message) => {
        setDeleteDialog({ open: true, type, item, message });
    };

    const openAddIssue = (vehicleId = "") => setAddIssue({ open: true, vehicleId });
    const openAddService = (vehicleId = "") => setAddService({ open: true, vehicleId });
    const openAddCleaning = (vehicleId = "") => setAddCleaning({ open: true, vehicleId });
    const openAddInspection = (vehicleId = "") => setAddInspection({ open: true, vehicleId });

    // Per-tab "Add" buttons (Fleet and Insurance don't have standalone add)
    const tabAddConfig = [
        null,
        { label: "Report Issue", handler: () => openAddIssue() },
        { label: "Log Service", handler: () => openAddService() },
        { label: "Log Cleaning", handler: () => openAddCleaning() },
        { label: "Log Inspection", handler: () => openAddInspection() },
        null,
    ];
    const currentAdd = tabAddConfig[tab];

    return (
        <Box sx={{ maxWidth: 600, mx: "auto", pb: 10 }}>
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 0.5, borderBottom: 1, borderColor: "divider" }}
            >
                {TABS.map((t) => (
                    <Tab
                        key={t}
                        label={t}
                        sx={{
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 600,
                            fontSize: "0.78rem",
                            minWidth: 0,
                            px: 1.5,
                        }}
                    />
                ))}
            </Tabs>

            {loading ? (
                <Box sx={{ pt: 2 }}>
                    {[1, 2, 3].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            height={72}
                            sx={{ mb: 1, borderRadius: 2 }}
                        />
                    ))}
                </Box>
            ) : (
                <>
                    {/* Add button row */}
                    {currentAdd && (
                        <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1.5, pb: 0.5 }}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={currentAdd.handler}
                                disableElevation
                            >
                                {currentAdd.label}
                            </Button>
                        </Stack>
                    )}

                    {/* Fleet */}
                    <TabPanel value={tab} index={0}>
                        {vehicles.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
                                No vehicles found.
                            </Typography>
                        ) : (
                            vehicles.map((v) => (
                                <VehicleCard
                                    key={v.id}
                                    vehicle={v}
                                    issues={issues}
                                    onReportIssue={openAddIssue}
                                    onLogService={openAddService}
                                    onLogCleaning={openAddCleaning}
                                    onLogInspection={openAddInspection}
                                />
                            ))
                        )}
                    </TabPanel>

                    {/* Issues */}
                    <TabPanel value={tab} index={1}>
                        {issues.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
                                No issues recorded.
                            </Typography>
                        ) : (
                            issues.map((i) => (
                                <IssueCard
                                    key={i.id}
                                    issue={i}
                                    canDelete={canManage}
                                    onDelete={(item) =>
                                        confirmDelete(
                                            "issue",
                                            item,
                                            `Permanently delete issue: "${item.description}"?`
                                        )
                                    }
                                />
                            ))
                        )}
                    </TabPanel>

                    {/* Services */}
                    <TabPanel value={tab} index={2}>
                        {services.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
                                No services recorded.
                            </Typography>
                        ) : (
                            services.map((s) => (
                                <ServiceCard
                                    key={s.id}
                                    service={s}
                                    canDelete={canManage}
                                    onDelete={(item) =>
                                        confirmDelete(
                                            "service",
                                            item,
                                            `Permanently delete service: "${item.description}"?`
                                        )
                                    }
                                />
                            ))
                        )}
                    </TabPanel>

                    {/* Cleanings */}
                    <TabPanel value={tab} index={3}>
                        {cleanings.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
                                No cleanings recorded.
                            </Typography>
                        ) : (
                            cleanings.map((c) => (
                                <CleaningCard
                                    key={c.id}
                                    cleaning={c}
                                    canDelete={canManage}
                                    onDelete={(item) =>
                                        confirmDelete(
                                            "cleaning",
                                            item,
                                            `Permanently delete cleaning for ${item.vehicle?.nickname}?`
                                        )
                                    }
                                />
                            ))
                        )}
                    </TabPanel>

                    {/* Inspections */}
                    <TabPanel value={tab} index={4}>
                        {inspections.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
                                No inspections recorded.
                            </Typography>
                        ) : (
                            inspections.map((i) => (
                                <InspectionCard
                                    key={i.id}
                                    inspection={i}
                                    canDelete={canManage}
                                    onDelete={(item) =>
                                        confirmDelete(
                                            "inspection",
                                            item,
                                            `Permanently delete inspection: "${item.description}"?`
                                        )
                                    }
                                />
                            ))
                        )}
                    </TabPanel>

                    {/* Insurance */}
                    <TabPanel value={tab} index={5}>
                        <InsurancePanel canManage={canManage} toast={toast} />
                    </TabPanel>
                </>
            )}

            {/* Dialogs */}
            <AddIssueDialog
                open={addIssue.open}
                onClose={() => setAddIssue({ open: false, vehicleId: "" })}
                onSave={handleAddIssue}
                vehicles={vehicles}
                initialVehicleId={addIssue.vehicleId}
            />
            <AddServiceDialog
                open={addService.open}
                onClose={() => setAddService({ open: false, vehicleId: "" })}
                onSave={handleAddService}
                vehicles={vehicles}
                initialVehicleId={addService.vehicleId}
            />
            <AddCleaningDialog
                open={addCleaning.open}
                onClose={() => setAddCleaning({ open: false, vehicleId: "" })}
                onSave={handleAddCleaning}
                vehicles={vehicles}
                initialVehicleId={addCleaning.vehicleId}
            />
            <AddInspectionDialog
                open={addInspection.open}
                onClose={() => setAddInspection({ open: false, vehicleId: "" })}
                onSave={handleAddInspection}
                vehicles={vehicles}
                initialVehicleId={addInspection.vehicleId}
            />
            <DeleteDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog((d) => ({ ...d, open: false }))}
                onConfirm={handleDelete}
                message={deleteDialog.message}
            />

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snack.severity}
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
